import { WhiteInstantSearchBuilder, Pagination } from '@white-nl/instantsearch-components';

import CatalogHit from '../hits/CatalogHit';
import HierarchicalMenuWithCategories from '../refinements/HierarchicalMenuWithCategories';
import RefinementList from '../../components/refinements/RefinementList';
import RangeSlider from '../../components/refinements/RangeSlider';
import CurrentRefinements from '../../components/refinements/CurrentRefinements';
import SortBy from '../../components/refinements/SortBy';
import Stats from '../../components/result/Stats';
import ProductSkeleton from '../../components/result/ProductSkeleton';
import NoResults from '../../components/result/NoResults';
import Hidden from '../../components/ui/Hidden';
import { delayedDataLayerPush } from '../../../components/DataLayer';

type ProductForGA4 = {
  id: string | number,
  title: string,
  productCode: string,
  defaultPrice: number,
};

export default function initWhiteInstantSearchCatalog() {
  const catalog = document.getElementById('white-instant-search-catalog');
  const catalogUsps = document.getElementById('white-instant-search-catalog-usps');

  if (!catalog) {
    return;
  }

  const {
    indexName,
    publicUrl,
    refreshUrl,
    currentSite,
    currentCategoryFullPath,
    currentCategoryPath,
    currentCategoryId,
    currentCategoryList,
    baseUrl,
    nodeUrl,
  } = catalog.dataset;

  if (!indexName
    || !publicUrl
    || !refreshUrl
    || !currentSite
    || !currentCategoryFullPath
    || !currentCategoryPath
    || !currentCategoryId
    || !currentCategoryList
    || !baseUrl
    || !nodeUrl
  ) {
    return;
  }

  const processedGA4Products = new Set<string | number>();
  let newGA4Products: ProductForGA4[] = [];

  const handleProductVisible = ({
    id,
    title,
    productCode,
    defaultPrice,
  }: ProductForGA4) => {
    if (processedGA4Products.has(id)) {
      return;
    }
    processedGA4Products.add(id);
    newGA4Products.push({
      id,
      title,
      productCode,
      defaultPrice,
    });
    delayedDataLayerPush({
      event: 'view_item_list',
      ecommerce: {
        item_list_id: currentCategoryId,
        item_list_name: currentCategoryList,
        items: newGA4Products.map((product) => ({
          item_name: product.title,
          item_id: product.productCode,
          price: product.defaultPrice,
        })),
      },
      event_callback: () => {
        newGA4Products = [];
      },
      event_timeout: 1000,
    });
  };

  const whiteInstantSearchCatalog = new WhiteInstantSearchBuilder();
  whiteInstantSearchCatalog
    .withSearchkit(
      indexName,
      publicUrl,
      '',
      refreshUrl,
    )
    .withWhiteInstantSearchRouting({
      baseUrl,
      addToPath: true,
      addTrailingSlashesToUrls: true,
      pathAttribute: 'category',
      mapping: {
        category: {
          [currentCategoryFullPath]: currentCategoryPath,
        },
      },
    })
    .where(`siteId:${currentSite}`)
    .setHitsPerPage(24)
    .setScrolling({
      includeAttributes: ['page'],
    })
    .addHitComponent(
      [
        {
          component: CatalogHit,
          loadingComponent: ProductSkeleton,
          noResultsComponent: NoResults,
        },
      ],
    )
    .addHierarchicalMenu(
      [
        {
          settings: {
            attributes: [
              'category',
              'category1',
              'category2',
              'category3',
              'category4',
            ],
            separator: '/',
            limit: 999,
            panelSettings: {
              header: Craft.t('catalog', 'Categories'),
            },
          },
          component: HierarchicalMenuWithCategories,
        },
      ],
    )
    .addRefinementList(
      [
        {
          settings: {
            attribute: 'isOnSale',
            sortBy: ['name:desc'],
            limit: 999,
            transformItems: (items) => items.map((item) => ({
              ...item,
              label: (item.label === '1') ? Craft.t('catalog', 'Yes') : Craft.t('catalog', 'No'),
            })),
            panelSettings: {
              header: Craft.t('catalog', 'Sale'),
            },
          },
          component: RefinementList,
        },
        {
          settings: {
            attribute: 'productFilterColor.title',
            sortBy: ['count:desc', 'name:asc'],
            limit: 999,
            panelSettings: {
              header: Craft.t('catalog', 'Color'),
            },
          },
          component: RefinementList,
        },
        {
          settings: {
            attribute: 'productFilterSuitableFor.title',
            sortBy: ['count:desc', 'name:asc'],
            limit: 999,
            panelSettings: {
              header: Craft.t('catalog', 'Suitable for'),
            },
          },
          component: RefinementList,
        },
        {
          settings: {
            attribute: 'showTempOutOfStockLabel',
            sortBy: ['name:desc'],
            limit: 999,
            transformItems: (items) => items.map((item) => ({
              ...item,
              label: (item.label === '1') ? Craft.t('catalog', 'Yes') : Craft.t('catalog', 'No'),
            })),
            panelSettings: {
              header: Craft.t('catalog', 'Temporarily sold out'),
            },
          },
          component: RefinementList,
        },
      ],
    )
    .addRangeSlider(
      [
        {
          settings: {
            attribute: 'defaultPrice',
            showThumbValues: true,
            format: {
              from: Craft.formatCurrency,
              to: Craft.formatCurrency,
            },
            panelSettings: {
              header: Craft.t('catalog', 'Price'),
            },
          },
          component: RangeSlider,
        },
      ],
    )
    .addPaginationComponent(
      [
        {
          settings: {
            classNames: {
              list: 'flex items-center justify-end gap-1 md:gap-2 mt-20',
              item: 'text-sm lg:text-base font-semibold text-gray-950 cursor-pointer border border-gray-950/10 lg:border-current rounded-sm inline-flex items-center hover:border-pink-400 hover:text-pink-400',
              link: 'flex items-center justify-center w-8 h-8 lg:w-10 lg:h-10 no-underline',
              previousPageItem: 'xs:hidden sm:inline-flex',
              nextPageItem: 'xs:hidden sm:inline-flex',
              selectedItem: 'border-pink-400 text-pink-400',
              disabledItem: '!hidden',
            },
          },
          component: Pagination,
        },
      ],
    )
    .addCurrentRefinementsComponent(
      [
        {
          settings: {
            excludedAttributes: ['category'],
            transformItems: (items) => items.map((item) => {
              if (item.attribute === 'showTempOutOfStockLabel') {
                return {
                  ...item,
                  refinements: item.refinements.map((refinement) => ({
                    ...refinement,
                    label: refinement.label === '1'
                      ? `${Craft.t('catalog', 'Temporarily sold out')}: ${Craft.t('catalog', 'Yes')}`
                      : `${Craft.t('catalog', 'Temporarily sold out')}: ${Craft.t('catalog', 'No')}`,
                  })),
                };
              }
              if (item.attribute === 'isOnSale') {
                return {
                  ...item,
                  refinements: item.refinements.map((refinement) => ({
                    ...refinement,
                    label: refinement.label === '1'
                      ? `${Craft.t('catalog', 'Sale')}: ${Craft.t('catalog', 'Yes')}`
                      : `${Craft.t('catalog', 'Sale')}: ${Craft.t('catalog', 'No')}`,
                  })),
                };
              }
              if (item.attribute === 'defaultPrice') {
                return {
                  ...item,
                  refinements: item.refinements.map((refinement) => ({
                    ...refinement,
                    label: `${refinement.label[0]} ${Craft.formatCurrency(refinement.value)}`,
                  })),
                };
              }
              return item;
            }),
          },
          component: CurrentRefinements,
        },
      ],
    )
    .addStatsComponent(
      [
        {
          settings: {
            translations: {
              resultsFound: Craft.t('catalog', '{qty, plural, =1{1 result} other{# results}}'),
            },
          },
          component: Stats,
        },
      ],
    )
    .addStatsComponent(
      [
        {
          settings: {
            translations: {
              resultsFound: Craft.t('catalog', 'Filter {qty, plural, =1{1 result} other{# results}}'),
            },
          },
          component: Stats,
        },
      ],
      '#white-instant-search-stats--filter-results',
    )
    .addStatsComponent(
      [
        {
          settings: {
            translations: {
              resultsFound: Craft.t('catalog', 'Show {qty, plural, =1{1 result} other{# results}}'),
            },
          },
          component: Stats,
        },
      ],
      '#white-instant-search-stats--show-results',
    )
    .addSortByComponent(
      [
        {
          settings: {
            items: [
              {
                label: Craft.t('catalog', 'Latest'),
                value: indexName,
              },
              {
                label: Craft.t('catalog', 'Name (z-a)'),
                value: `${indexName}products_by_title_desc`,
              },
              {
                label: Craft.t('catalog', 'Name (a-z)'),
                value: `${indexName}products_by_title_asc`,
              },
              {
                label: Craft.t('catalog', 'Highest price'),
                value: `${indexName}products_by_price_desc`,
              },
              {
                label: Craft.t('catalog', 'Lowest price'),
                value: `${indexName}products_by_price_asc`,
              },
            ],

          },
          component: SortBy,
        },
      ],
    )
    .addSearchingComponent(
      [
        {
          settings: {},
          component: Hidden,
        },
      ],
    )
    .addAdditionalData({
      siteId: currentSite,
      baseUrl,
      nodeUrl,
      currentCategoryId,
      currentCategoryList,
      usps: catalogUsps ? catalogUsps.innerHTML : '',
      onProductVisible: handleProductVisible,
    })
    .build()
    .show();
}
